import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Leaf, Clock, Wrench, TrendingDown } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { T } from "@/components/T";

function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll('.reveal');
    const observer = new IntersectionObserver(
      (entries) => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold: 0.06, rootMargin: '0px 0px -40px 0px' }
    );
    els.forEach(el => observer.observe(el));
    return () => els.forEach(el => observer.unobserve(el));
  });
}

const fakten = [
  {
    icon: <Leaf size={20} />,
    titel_de: "CO₂-Speicher",
    titel_it: "Stoccaggio CO₂",
    text_de: "Ein Kubikmeter Holz speichert ca. 1 Tonne CO₂. Das Bauen mit Holz ist keine Selbstberuhigung – es ist messbar besser.",
    text_it: "Un metro cubo di legno immagazzina circa 1 tonnellata di CO₂. Costruire in legno non è un'autoconsolazione – è misurabilmente meglio."
  },
  {
    icon: <Clock size={20} />,
    titel_de: "Langlebigkeit",
    titel_it: "Longevità",
    text_de: "Richtig geplante Holzkonstruktionen halten Jahrhunderte. Das Münster in Ulm beweist es seit 1377. Gute Details entscheiden.",
    text_it: "Le costruzioni in legno pianificate correttamente durano secoli. Il Münster di Ulm lo dimostra dal 1377. I buoni dettagli decidono."
  },
  {
    icon: <Wrench size={20} />,
    titel_de: "Vorfertigung",
    titel_it: "Prefabbricazione",
    text_de: "Vorfertigung reduziert Materialverluste auf der Baustelle erheblich. Mehr Präzision, weniger Abfall – aus konstruktiven Gründen.",
    text_it: "La prefabbricazione riduce notevolmente le perdite di materiale in cantiere. Più precisione, meno spreco – per ragioni costruttive."
  },
  {
    icon: <TrendingDown size={20} />,
    titel_de: "Sanierung vor Neubau",
    titel_it: "Risanamento prima della nuova costruzione",
    text_de: "Bestand erhalten ist nachhaltiger als abreißen und neu bauen. Wir unterstützen diese Entscheidung mit Kompetenz.",
    text_it: "Preservare il patrimonio edilizio è più sostenibile che demolire e costruire da zero. Supportiamo questa scelta con competenza."
  }
];

export default function Nachhaltigkeit() {
  const { lang } = useLanguage();
  useReveal();

  return (
    <div className="pt-20 md:pt-24">
      {/* Hero */}
      <section className="relative h-[50vh] overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1748007702945-cca7b75d96ca?w=1800&q=80"
          alt="Holzbau Nachhaltigkeit"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-hb-dark/70 to-hb-dark/20" />
        <div className="absolute bottom-0 left-0 right-0 container-hb pb-12">
          <span className="label-text text-white/60 block mb-4">
            <T de="Nachhaltigkeit" it="Sostenibilità" />
          </span>
          <h1 className="text-5xl sm:text-6xl font-manrope font-extrabold text-white leading-tight" data-testid="nachhaltigkeit-heading">
            <T de="Bauen, das bleibt." it="Costruire per durare." />
          </h1>
        </div>
      </section>

      {/* Einleitung */}
      <section className="section-gap bg-white" data-testid="nachhaltigkeit-content">
        <div className="container-hb">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <span className="label-text text-hb-wood block mb-5 reveal">
                <T de="Unsere Haltung" it="Il nostro approccio" />
              </span>
              <h2 className="text-3xl sm:text-4xl font-manrope font-extrabold text-hb-dark mb-6 reveal reveal-delay-1">
                <T de="Kein Marketing. Nur Fakten." it="Nessun marketing. Solo fatti." />
              </h2>
              <div className="space-y-4 reveal reveal-delay-2">
                <p className="text-base text-hb-text font-inter leading-relaxed">
                  <T de="Wir bauen mit Holz. Das ist keine Marketingentscheidung – das ist unser Handwerk. Und Holz ist, bei aller Bescheidenheit, eines der intelligentesten Baumaterialien, die es gibt."
                     it="Costruiamo con il legno. Non è una scelta di marketing – è il nostro mestiere. E il legno è, con tutta modestia, uno dei materiali da costruzione più intelligenti che esistano." />
                </p>
                <p className="text-base text-hb-text font-inter leading-relaxed">
                  <T de="Es ist ein nachwachsender Rohstoff. Es speichert CO₂. Es hat eine hervorragende Wärmedämmung. Es lässt sich präzise planen und effizient verarbeiten. Und wenn es gut gebaut ist, hält es für Generationen."
                     it="È una materia prima rinnovabile. Immagazzina CO₂. Ha un eccellente isolamento termico. Può essere pianificato con precisione e lavorato in modo efficiente. E se è ben costruito, dura per generazioni." />
                </p>
                <p className="text-base text-hb-text font-inter leading-relaxed">
                  <T de="Wir stehen für Qualität, die sich lohnt – auch auf lange Sicht."
                     it="Rappresentiamo una qualità che vale la pena – anche sul lungo periodo." />
                </p>
              </div>
            </div>
            <div className="bg-hb-off-white p-10">
              <h3 className="text-xs tracking-widest uppercase text-hb-muted font-semibold mb-6">
                <T de="Warum Holz?" it="Perché il legno?" />
              </h3>
              <ul className="space-y-5">
                {(lang === 'it' ? [
                  "Materia prima rinnovabile da foreste regionali controllate",
                  "Carbon neutral fino a carbon positivo nell'analisi del ciclo di vita",
                  "Consumo energetico notevolmente inferiore nella produzione e lavorazione",
                  "Smontabile e riciclabile",
                  "L'isolamento termico naturale risparmia energia di riscaldamento",
                  "Eccellente bilancio ecologico rispetto al calcestruzzo e all'acciaio"
                ] : [
                  "Nachwachsender Rohstoff aus regional kontrollierten Wäldern",
                  "CO₂-neutral bis CO₂-positiv in der Lebenszyklusanalyse",
                  "Deutlich geringerer Energieeinsatz bei Herstellung und Verarbeitung",
                  "Rückbaubar und wiederverwertbar",
                  "Natürliche Wärmedämmung spart Heizenergie",
                  "Hervorragende Ökobilanz gegenüber Beton und Stahl"
                ]).map((p, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-hb-text font-inter">
                    <div className="w-1.5 h-1.5 rounded-full bg-hb-wood flex-shrink-0 mt-1.5" />
                    {p}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Fakten */}
      <section className="section-gap bg-hb-off-white" data-testid="fakten-section">
        <div className="container-hb">
          <span className="label-text text-hb-wood block mb-5 reveal">
            <T de="Konkret" it="Concretamente" />
          </span>
          <h2 className="section-title mb-14 reveal reveal-delay-1">
            <T de="Was Nachhaltigkeit bei uns bedeutet." it="Cosa significa sostenibilità per noi." />
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-hb-border">
            {fakten.map((f, i) => (
              <div key={i} className={`bg-white p-10 reveal reveal-delay-${(i % 2) + 1}`}>
                <div className="text-hb-wood mb-4">{f.icon}</div>
                <h3 className="font-manrope font-bold text-hb-dark text-xl mb-3">
                  {lang === 'it' ? f.titel_it : f.titel_de}
                </h3>
                <p className="text-sm text-hb-text font-inter leading-relaxed">
                  {lang === 'it' ? f.text_it : f.text_de}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vorfertigung */}
      <section className="section-gap bg-white" data-testid="vorfertigung-section">
        <div className="container-hb">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="aspect-[4/3] overflow-hidden">
              <img
                src="https://hafner-ec.com/wp-content/uploads/2025/04/Kohlbrenner-99-2500-400x300.jpg"
                alt="Vorfertigung"
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <span className="label-text text-hb-wood block mb-5 reveal">
                <T de="Vorfertigung" it="Prefabbricazione" />
              </span>
              <h2 className="text-3xl sm:text-4xl font-manrope font-extrabold text-hb-dark mb-6 reveal reveal-delay-1">
                <T de="Ressourcen schonen durch Präzision." it="Risparmiare risorse attraverso la precisione." />
              </h2>
              <p className="text-base text-hb-text font-inter leading-relaxed mb-4 reveal reveal-delay-2">
                <T de="Vorfertigung ist nicht nur ein Qualitätsmerkmal – es ist auch ein ökologisches Statement. Alles, was im Werk präzise zugeschnitten wird, erzeugt weniger Verschnitt, weniger Transport und weniger Lärm auf der Baustelle."
                   it="La prefabbricazione non è solo un indicatore di qualità – è anche una dichiarazione ecologica. Tutto ciò che viene tagliato con precisione in officina genera meno sfridi, meno trasporti e meno rumore in cantiere." />
              </p>
              <p className="text-base text-hb-text font-inter leading-relaxed reveal reveal-delay-3">
                <T de="Unsere Montagezeiten auf der Baustelle sind kurz, weil die Arbeit vorher sauber erledigt wird. Weniger Baustellenzeit bedeutet weniger Lärm, weniger Emissionen, weniger Aufwand für alle Beteiligten."
                   it="I nostri tempi di montaggio in cantiere sono brevi perché il lavoro viene svolto in anticipo in modo pulito. Meno tempo in cantiere significa meno rumore, meno emissioni, meno oneri per tutti i coinvolti." />
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-hb-dark">
        <div className="container-hb text-center">
          <h2 className="text-3xl font-manrope font-extrabold text-white mb-4">
            <T de="Nachhaltig bauen in Südtirol?" it="Costruire in modo sostenibile in Alto Adige?" />
          </h2>
          <p className="text-white/60 font-inter mb-8">
            <T de="Sprechen Sie mit uns über Ihr Projekt." it="Parlate con noi del vostro progetto." />
          </p>
          <Link
            to="/kontakt"
            data-testid="nachhaltigkeit-cta"
            className="btn-primary bg-white text-hb-dark hover:bg-hb-off-white"
          >
            <T de="Beratung anfragen" it="Richiedi una consulenza" />
          </Link>
        </div>
      </section>
    </div>
  );
}
